import React, { useMemo, useState } from 'react';
import { useDashboard } from '@/context/DashboardContext';
import { PIPELINE, STAGES, LOST_THIS_FY, AVG_DAYS_TO_CONVERT } from '@/data/pipeline';
import { PROGRAMS, PROGRAM_LIST } from '@/data/seed';
import { cr, crFull } from '@/lib/fmt';
import KpiCard from '@/components/KpiCard';
import { Chip, ProgramChip } from '@/components/UiBits';
import { FILTERS } from '@/constants/testIds';
import { Users, GitFork, TrendingUp, Target, Clock, MapPin, RotateCcw } from 'lucide-react';
import { BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';

export default function Pipeline() {
  const { PROGRAMS: P } = useDashboard();
  const [city, setCity]       = useState('');
  const [program, setProgram] = useState('');
  const [stage, setStage]     = useState('');
  const [kind, setKind]       = useState('');
  const [q, setQ]             = useState('');

  const activeCount = [city, program, stage, kind, q].filter(Boolean).length;
  const cities = useMemo(() => [...new Set(PIPELINE.map((x) => x.city))].sort(), []);

  const kindOf = (x) => x.kind || 'New';
  const rows = PIPELINE.filter((x) =>
    (!city || x.city === city) &&
    (!program || x.program === program) &&
    (!stage || x.stage === stage) &&
    (!kind || kindOf(x) === kind) &&
    (!q || x.org.toLowerCase().includes(q.toLowerCase()))
  );
  const open = rows.filter((x) => x.stage !== 'converted');
  const submittedish = rows.filter((x) => x.stage === 'submitted' || x.stage === 'provisional');
  const weighted = open.reduce((s, x) => s + x.val * STAGES.find((st) => st.key === x.stage).prob, 0);
  const won = rows.filter((x) => x.stage === 'converted');
  const winRate = (won.length / (won.length + LOST_THIS_FY)) * 100;

  const reset = () => { setCity(''); setProgram(''); setStage(''); setKind(''); setQ(''); };

  // Funnel bars (respect all filters except stage)
  const fRows = PIPELINE.filter((x) =>
    (!city || x.city === city) &&
    (!program || x.program === program) &&
    (!kind || kindOf(x) === kind) &&
    (!q || x.org.toLowerCase().includes(q.toLowerCase()))
  );
  const maxStageCount = Math.max(1, ...STAGES.map((s) => fRows.filter((x) => x.stage === s.key).length));

  // City chart
  const cityAgg = {};
  open.forEach((x) => (cityAgg[x.city] = (cityAgg[x.city] || 0) + x.val));
  const cityData = Object.entries(cityAgg).map(([k, v]) => ({ name: k, value: +v.toFixed(1) })).sort((a, b) => b.value - a.value);

  // Program donut
  const progVals = PROGRAM_LIST.map((k) => ({
    name: PROGRAMS[k].name,
    value: +open.filter((x) => x.program === k).reduce((s, x) => s + x.val, 0).toFixed(1),
    color: PROGRAMS[k].color,
  }));

  const aging = [...open].sort((a, b) => b.days - a.days).slice(0, 6);

  return (
    <>
      <div className="page-head">
        <div>
          <div className="overline">FY 25-26 · Fundraising</div>
          <h1>Fundraising Pipeline</h1>
          <div className="sub">
            Prospective donors, proposals in play and conversion — the proposal lifecycle
            (In Conversation → Submitted → Provisionally Approved → Converted).
          </div>
        </div>
      </div>

      <div className="filter-strip">
        <div className="filter-group">
          <label>Location</label>
          <select data-testid={FILTERS.pf.city} value={city} onChange={(e) => setCity(e.target.value)}>
            <option value="">All locations</option>
            {cities.map((c) => <option key={c}>{c}</option>)}
          </select>
        </div>
        <div className="filter-group">
          <label>Program</label>
          <select data-testid={FILTERS.pf.program} value={program} onChange={(e) => setProgram(e.target.value)}>
            <option value="">All programs</option>
            {PROGRAM_LIST.map((k) => <option key={k} value={k}>{PROGRAMS[k].name}</option>)}
          </select>
        </div>
        <div className="filter-group">
          <label>Stage</label>
          <select data-testid={FILTERS.pf.stage} value={stage} onChange={(e) => setStage(e.target.value)}>
            <option value="">All stages</option>
            {STAGES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
          </select>
        </div>
        <div className="filter-group">
          <label>Type</label>
          <select data-testid={FILTERS.pf.kind} value={kind} onChange={(e) => setKind(e.target.value)}>
            <option value="">New + renewals</option>
            <option>New</option>
            <option>Renewal</option>
          </select>
        </div>
        <div className="filter-group">
          <label>Search</label>
          <input data-testid={FILTERS.pf.search} type="search" placeholder="Search prospect…" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        {activeCount > 0 && (
          <button className="btn-reset" data-testid={FILTERS.pf.reset} onClick={reset} style={{ display: 'inline-flex' }}>
            <RotateCcw size={13} /> Reset Filters <span className="badge-count">{activeCount}</span>
          </button>
        )}
      </div>

      <div className="kpi-row">
        <KpiCard icon={Users}      iconCls="ic-gold"  value={rows.filter((x) => x.stage === 'conversation').length} label="Prospects in Conversation" delta={['up','4 new this quarter']} />
        <KpiCard icon={GitFork}    iconCls="ic-blue"  value={cr(submittedish.reduce((s, x) => s + x.val, 0))}       label="Proposal Value Submitted"  delta={['flat', submittedish.length + ' with donors']} />
        <KpiCard icon={TrendingUp} iconCls="ic-amber" value={cr(weighted)}                                          label="Weighted Pipeline"          delta={['flat','probability-adjusted']} />
        <KpiCard icon={Target}     iconCls="ic-green" value={cr(won.reduce((s, x) => s + x.val, 0))}                label="Converted FY-to-date"       delta={['up', won.length + ' new agreements']} />
        <KpiCard icon={Clock}      iconCls="ic-red"   value={(open.length ? Math.max(...open.map((x) => x.days)) : 0) + 'd'} label="Oldest Open Proposal" delta={['down','days in current stage']} />
      </div>

      <div className="grid-32">
        <div className="card">
          <div className="card-head">
            <div>
              <div className="card-title">Conversion Funnel</div>
              <div className="card-sub">Count &amp; value by stage · FY-to-date</div>
            </div>
          </div>
          <div className="funnel">
            {STAGES.map((s, i) => {
              const list = fRows.filter((x) => x.stage === s.key);
              const opacity = 0.35 + 0.65 * (list.length / maxStageCount);
              return (
                <React.Fragment key={s.key}>
                  <div className="funnel-stage">
                    <div className="funnel-bar" style={{ background: s.color, opacity }}>
                      <div className="fs-count">{list.length}</div>
                      <div className="fs-val" title={crFull(list.reduce((sm, x) => sm + x.val, 0))}>
                        {cr(list.reduce((sm, x) => sm + x.val, 0))}
                      </div>
                    </div>
                    <div className="funnel-label">{s.label}</div>
                    <div className="funnel-conv">{(s.prob * 100).toFixed(0)}% probability</div>
                  </div>
                  {i < STAGES.length - 1 && <div className="funnel-arrow">›</div>}
                </React.Fragment>
              );
            })}
          </div>
          <div className="mt-4 pt-3.5 border-t border-[color:var(--gray-100)] text-[12.5px] text-[color:var(--gray-500)] flex flex-wrap gap-5">
            <span>Win rate FY-to-date: <b style={{ color: 'var(--green-600)' }}>{winRate.toFixed(0)}%</b> ({won.length} won · {LOST_THIS_FY} deferred/rejected)</span>
            <span>Avg. days to convert: <b style={{ color: 'var(--gray-800)' }}>{AVG_DAYS_TO_CONVERT}</b></span>
            <span>Weighted pipeline: <b style={{ color: 'var(--gray-800)' }}>{cr(weighted)}</b></span>
          </div>
        </div>

        <div className="card">
          <div className="card-head">
            <div>
              <div className="card-title">Proposal Value by Location</div>
              <div className="card-sub">Open pipeline ₹ Cr</div>
            </div>
          </div>
          <div style={{ height: 260 }}>
            <ResponsiveContainer>
              <BarChart data={cityData} layout="vertical" margin={{ top: 5, right: 20, left: 8, bottom: 5 }}>
                <CartesianGrid stroke="#F3F4F6" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 11, fill: '#6B7280' }} tickFormatter={(v) => `₹${v}`} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fill: '#374151' }} width={90} />
                <Tooltip formatter={(v) => cr(v) + ' open pipeline'} contentStyle={{ borderRadius: 8, border: '1px solid #E5E7EB', fontSize: 12 }} />
                <Bar dataKey="value" fill="#FFC20E" radius={[0, 6, 6, 0]} maxBarSize={22} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="grid-2">
        <div className="card">
          <div className="card-head">
            <div>
              <div className="card-title">Proposal Value by Program</div>
              <div className="card-sub">Open pipeline share</div>
            </div>
          </div>
          <div style={{ height: 210 }}>
            <ResponsiveContainer>
              <PieChart>
                <Pie data={progVals} innerRadius={54} outerRadius={84} dataKey="value" paddingAngle={2}>
                  {progVals.map((e, i) => <Cell key={i} fill={e.color} stroke="#fff" strokeWidth={2} />)}
                </Pie>
                <Tooltip formatter={(v) => cr(v)} contentStyle={{ borderRadius: 8, border: '1px solid #E5E7EB', fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="flex flex-wrap gap-2 mt-2">
            {progVals.map((p) => (
              <span key={p.name} className="text-[11px] text-[color:var(--gray-600)]">
                <span className="inline-block w-2 h-2 rounded-full mr-1.5" style={{ background: p.color }} />
                {p.name} — {cr(p.value)}
              </span>
            ))}
          </div>
        </div>

        <div className="card">
          <div className="card-head">
            <div>
              <div className="card-title">Proposal Aging</div>
              <div className="card-sub">Longest in current stage — follow-up candidates</div>
            </div>
          </div>
          <div className="tbl-wrap">
            <table className="tbl">
              <thead>
                <tr><th>Prospect</th><th>Stage</th><th className="num">Value</th><th className="num">Days in Stage</th></tr>
              </thead>
              <tbody>
                {aging.map((x, i) => (
                  <tr key={i}>
                    <td>
                      <span className="ent">
                        {x.org}{x.kind === 'Renewal' && <span className="chip gold ml-1.5">Renewal</span>}
                      </span>
                      <span className="sub">{x.city} · {x.owner}</span>
                    </td>
                    <td><Chip kind={x.stage === 'provisional' ? 'Active' : x.stage === 'submitted' ? 'New' : 'closed'}>{STAGES.find((s) => s.key === x.stage).label}</Chip></td>
                    <td className="num" title={crFull(x.val)}>{cr(x.val)}</td>
                    <td className="num" style={{ color: x.days > 45 ? 'var(--red-600)' : 'var(--gray-800)' }}>
                      {x.days}{x.days > 45 ? ' ⚠' : ''}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="sec-title">Stage board</div>
      <div className="board">
        {STAGES.map((s) => {
          const list = rows.filter((x) => x.stage === s.key);
          return (
            <div key={s.key} className="board-col">
              <div className="board-col-head">
                <span className="t"><span className="dot" style={{ background: s.color }} />{s.label}</span>
                <span className="sum">{list.length} · {cr(list.reduce((sm, x) => sm + x.val, 0))}</span>
              </div>
              {list.length === 0 && (
                <div className="empty-state" style={{ padding: 20 }}>
                  <div className="empty-sub">No proposals match filters.</div>
                </div>
              )}
              {list.map((x, i) => (
                <div key={i} className="prop-card">
                  <div className="pc-name">{x.org}</div>
                  <div className="pc-meta">
                    <ProgramChip program={x.program} PROGRAMS={P} />
                    {x.kind === 'Renewal' && <span className="chip gold">Renewal</span>}
                    <span className="text-[11px] text-[color:var(--gray-500)] inline-flex items-center gap-1">
                      <MapPin size={10} /> {x.city}
                    </span>
                  </div>
                  <div className="pc-val" title={crFull(x.val)}>{cr(x.val)}</div>
                  <div className="pc-foot">
                    <span>{x.owner}</span>
                    <span className={x.days > 45 ? 'aging' : ''}>
                      {x.stage === 'converted' ? 'Signed ' + new Date(x.close).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }) : x.days + 'd in stage'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          );
        })}
      </div>
    </>
  );
}
